import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DraggableDialog, DraggableDialogContent, DraggableDialogDescription, DraggableDialogHeader, DraggableDialogTitle, DraggableDialogFooter } from '../components/ui/draggable-dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Plus, Phone, Mail, Calendar, FileText, X, User, DollarSign, Image, File, Clock, MessageSquare, Send, ExternalLink, Save, Edit2, ClipboardList, Activity, MapPin, StickyNote, Wrench, Share2, AtSign, Users, CalendarPlus, Ruler, Settings, ArrowRight, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { filterJobsByPermissions, getScopeName, getDataScope } from '../utils/permissions';
import { ALL_LEAD_STAGES, PROJECT_STAGES, UNMAPPED_STAGE, getStageForBPStatus, LEGACY_STAGE_MAPPING } from '../config/boardFlowsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Use BP Status Parity stages for board columns
// For JOBS/PROJECTS board, use PROJECT_STAGES
const STAGES = PROJECT_STAGES.filter(stage => !stage.isArchive).map(stage => ({
  id: stage.id,
  name: stage.name,
  color: stage.color || 'bg-slate-200 dark:bg-slate-800'
}));

// Helper to normalize job stage to valid BP stage
const normalizeJobStage = (job) => {
  // If job has a stage that exists in our STAGES, return it
  if (STAGES.find(s => s.id === job.stage)) {
    return job.stage;
  }
  
  // Check legacy mapping
  if (LEGACY_STAGE_MAPPING[job.stage]) {
    const mapped = LEGACY_STAGE_MAPPING[job.stage];
    if (STAGES.find(s => s.id === mapped)) {
      return mapped;
    }
  }
  
  // If job has bp_status_name, try to map it
  if (job.bp_status_name) {
    const stageConfig = getStageForBPStatus(job.bp_status_name, 'project');
    if (stageConfig && STAGES.find(s => s.id === stageConfig.id)) {
      return stageConfig.id;
    }
  }
  
  // Default to unmapped
  return 'unmapped';
};

export const Board = () => {
  const navigate = useNavigate();
  const { user, viewAsRole, viewAsUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // Store all jobs for filtering
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const [newNote, setNewNote] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [dialogTab, setDialogTab] = useState('overview');
  const [crews, setCrews] = useState([]);
  const [editingOpsField, setEditingOpsField] = useState(null);
  const [opsEditValue, setOpsEditValue] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    job_type: '',
    estimated_value: '',
    square_footage: '',
    first_visit_discount: '',
    demo_date: '',
    install_date: '',
    priority: 'medium',
    assigned_to: '',
    assigned_team: '',
    market_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, usersRes, teamsRes, marketsRes, crewsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/markets`),
        axios.get(`${API}/crews`).catch(() => ({ data: [] }))
      ]);
      setAllJobs(jobsRes.data); // Store all jobs
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setMarkets(marketsRes.data);
      setCrews(crewsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load board data');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilter = useCallback(() => {
    if (!allJobs || allJobs.length === 0) {
      setJobs([]);
      return;
    }

    // Determine the effective user
    let effectiveUser = user;

    // If testing as a different role/user
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) {
        effectiveUser = testUser;
      }
    }

    // Use the comprehensive permissions system
    const filteredJobs = filterJobsByPermissions(allJobs, effectiveUser, users);
    setJobs(filteredJobs);
    
    // Show informative message about data scope
    const scope = getDataScope(effectiveUser?.role);
    const scopeName = getScopeName(scope);
    
    if (filteredJobs.length === 0 && allJobs.length > 0) {
      toast.info(`No jobs visible with ${scopeName} permissions`);
    } else if (filteredJobs.length < allJobs.length) {
      console.log(`Filtered to ${filteredJobs.length} jobs (${scopeName} view)`);
    }
  }, [allJobs, user, viewAsUser, viewAsRole, users]);

  // Apply role-based filtering when dependencies change
  useEffect(() => {
    applyRoleBasedFilter();
  }, [applyRoleBasedFilter]);

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    console.log('Creating job with form data:', formData);
    
    try {
      const jobData = {
        ...formData,
        estimated_value: formData.estimated_value && formData.estimated_value.trim() !== '' ? parseFloat(formData.estimated_value) : null,
        square_footage: formData.square_footage && formData.square_footage.toString().trim() !== '' ? parseInt(formData.square_footage) : null,
        first_visit_discount: formData.first_visit_discount && formData.first_visit_discount.trim() !== '' ? parseFloat(formData.first_visit_discount) : null,
        stage: 'lead'
      };
      
      console.log('Sending job data:', jobData);
      
      const response = await axios.post(`${API}/jobs`, jobData);
      console.log('Job created successfully:', response.data);
      
      toast.success('Job created successfully');
      setCreateDialogOpen(false);
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        job_type: '',
        estimated_value: '',
        square_footage: '',
        first_visit_discount: '',
        demo_date: '',
        install_date: '',
        priority: 'medium',
        assigned_to: '',
        assigned_team: '',
        market_id: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Create job error - Full error:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      let errorMessage = 'Failed to create job';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const [dragDirection, setDragDirection] = useState(null); // 'forward' or 'backward'

  const getStageIndex = (stageId) => {
    return STAGES.findIndex(s => s.id === stageId);
  };

  const handleDragStart = (e, job) => {
    setDraggedCard(job);
    setDragDirection(null);
    setIsDragging(true);
    setDragPosition({ x: e.clientX, y: e.clientY });
    e.dataTransfer.effectAllowed = 'move';
    
    // Hide the default ghost image by using a transparent pixel
    const transparentImg = document.createElement('img');
    transparentImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(transparentImg, 0, 0);
    
    // Use document-level dragover for smoother tracking
    const handleMouseMove = (evt) => {
      evt.preventDefault();
      setDragPosition({ x: evt.clientX, y: evt.clientY });
    };
    
    document.addEventListener('dragover', handleMouseMove);
    
    window._dragCleanup = () => {
      document.removeEventListener('dragover', handleMouseMove);
    };
  };

  const handleDrag = (e) => {
    // Handled by document-level listener
  };

  const handleDragEnd = (e) => {
    if (window._dragCleanup) {
      window._dragCleanup();
      window._dragCleanup = null;
    }
    setIsDragging(false);
    setDraggedCard(null);
    setDragOverStage(null);
    setDragDirection(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (stageId) => {
    setDragOverStage(stageId);
    if (draggedCard) {
      const currentIndex = getStageIndex(draggedCard.stage);
      const targetIndex = getStageIndex(stageId);
      if (targetIndex > currentIndex) {
        setDragDirection('forward');
      } else if (targetIndex < currentIndex) {
        setDragDirection('backward');
      } else {
        setDragDirection(null);
      }
    }
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedCard || draggedCard.stage === targetStage) {
      setDragOverStage(null);
      setIsDragging(false);
      setDraggedCard(null);
      setDragDirection(null);
      return;
    }

    const movedJob = draggedCard;
    const previousStage = movedJob.stage;
    
    // Clear drag state immediately
    setDragOverStage(null);
    setIsDragging(false);
    setDraggedCard(null);
    setDragDirection(null);
    
    // Optimistic update - immediately move card in UI
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === movedJob.id 
          ? { ...job, stage: targetStage, status: targetStage }
          : job
      )
    );
    setAllJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === movedJob.id 
          ? { ...job, stage: targetStage, status: targetStage }
          : job
      )
    );

    try {
      await axios.patch(`${API}/jobs/${movedJob.id}`, {
        stage: targetStage,
        status: targetStage
      });
      toast.success(`Moved to ${STAGES.find(s => s.id === targetStage)?.name}`);
    } catch (error) {
      // Revert on error
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === movedJob.id 
            ? { ...job, stage: previousStage, status: previousStage }
            : job
        )
      );
      setAllJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === movedJob.id 
            ? { ...job, stage: previousStage, status: previousStage }
            : job
        )
      );
      console.error('Update job stage error:', error.response?.data);
      let errorMessage = 'Failed to update job stage';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const openJobDetails = async (job) => {
    setSelectedJob(job);
    setNewNote('');
    setIsEditing(false);
    setEditForm({
      customer_name: job.customer_name || '',
      customer_phone: job.customer_phone || '',
      customer_email: job.customer_email || '',
      address: job.address || '',
      city: job.city || '',
      state: job.state || '',
      zip_code: job.zip_code || '',
      job_type: job.job_type || '',
      estimated_value: job.estimated_value || '',
      square_footage: job.square_footage || '',
      priority: job.priority || 'medium',
      demo_date: job.demo_date || '',
      install_date: job.install_date || '',
    });
    setSmsMessage('');
    setDetailDialogOpen(true);
    
    // Fetch conversations for this job
    try {
      const response = await axios.get(`${API}/conversations?job_id=${job.id}`);
      setConversations(response.data || []);
    } catch (error) {
      console.log('No conversations found');
      setConversations([]);
    }
  };

  const handleSaveEdit = async () => {
    try {
      // Convert values to proper types
      const updateData = {
        ...editForm,
        estimated_value: editForm.estimated_value ? parseFloat(editForm.estimated_value) : null,
        square_footage: editForm.square_footage ? parseInt(editForm.square_footage) : null,
      };
      
      // Remove empty/null values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' || updateData[key] === null) {
          delete updateData[key];
        }
      });
      
      await axios.patch(`${API}/jobs/${selectedJob.id}`, updateData);
      toast.success('Job updated successfully');
      setIsEditing(false);
      // Update local state
      const updatedJob = { ...selectedJob, ...updateData };
      setSelectedJob(updatedJob);
      setJobs(prevJobs => prevJobs.map(j => j.id === selectedJob.id ? updatedJob : j));
      setAllJobs(prevJobs => prevJobs.map(j => j.id === selectedJob.id ? updatedJob : j));
    } catch (error) {
      console.error('Update error:', error.response?.data || error);
      toast.error('Failed to update job');
    }
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim() || !selectedJob.customer_phone) return;
    
    setSendingSms(true);
    try {
      const response = await axios.post(`${API}/communications/send-sms`, {
        to_number: selectedJob.customer_phone,
        message: smsMessage,
        job_id: selectedJob.id,
        contact_id: selectedJob.customer_id
      });
      
      // Check response success field as well
      if (response.data?.success === false) {
        throw new Error(response.data?.message || 'Failed to send SMS');
      }
      
      toast.success('SMS sent successfully');
      setSmsMessage('');
      // Refresh conversations
      try {
        const convResponse = await axios.get(`${API}/communications/jobs/${selectedJob.id}/conversations`);
        setConversations(convResponse.data?.conversations || []);
      } catch (convError) {
        console.log('No conversations found for job');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail?.message 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to send SMS';
      toast.error(errorMessage);
    } finally {
      setSendingSms(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      // Extract @mentions from note
      const mentionRegex = /@(\S+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(newNote)) !== null) {
        mentions.push(match[1]);
      }
      
      await axios.post(`${API}/jobs/${selectedJob.id}/notes`, {
        note: newNote,
        mentions: mentions
      });
      
      // Send notifications to mentioned users
      if (mentions.length > 0) {
        const mentionedUsers = users.filter(u => 
          mentions.some(m => 
            u.email.toLowerCase().includes(m.toLowerCase()) ||
            `${u.first_name}${u.last_name}`.toLowerCase().includes(m.toLowerCase()) ||
            `${u.first_name}.${u.last_name}`.toLowerCase() === m.toLowerCase()
          )
        );
        
        for (const mentionedUser of mentionedUsers) {
          try {
            await axios.post(`${API}/notifications`, {
              user_id: mentionedUser.id,
              type: 'mention',
              title: `You were mentioned in Job #${selectedJob.job_number || selectedJob.id.slice(0, 8)}`,
              message: `${user?.first_name || 'Someone'} mentioned you in a note: "${newNote.substring(0, 100)}${newNote.length > 100 ? '...' : ''}"`,
              link: `/jobs/${selectedJob.id}?tab=notes`,
              job_id: selectedJob.id
            });
          } catch (err) {
            console.log('Notification send failed:', err);
          }
        }
        toast.success(`Note added and ${mentionedUsers.length} user(s) notified`);
      } else {
        toast.success('Note added successfully');
      }
      
      setNewNote('');
      // Refresh the job data
      const response = await axios.get(`${API}/jobs/${selectedJob.id}`);
      setSelectedJob(response.data);
      fetchData();
    } catch (error) {
      console.error('Add note error:', error.response?.data);
      toast.error('Failed to add note');
    }
  };

  const handleOpsFieldUpdate = async (field, value) => {
    try {
      // Handle special "none" value for crew assignment
      const actualValue = value === 'none' ? null : value;
      const updateData = { [field]: actualValue };
      await axios.patch(`${API}/jobs/${selectedJob.id}`, updateData);
      toast.success('Updated successfully');
      setEditingOpsField(null);
      setOpsEditValue('');
      // Refresh job data
      const response = await axios.get(`${API}/jobs/${selectedJob.id}`);
      setSelectedJob(response.data);
      setJobs(prevJobs => prevJobs.map(j => j.id === selectedJob.id ? response.data : j));
      setAllJobs(prevJobs => prevJobs.map(j => j.id === selectedJob.id ? response.data : j));
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-600';
      case 'high': return 'bg-orange-600';
      case 'medium': return 'bg-yellow-600';
      case 'low': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  const getDaysInStatus = (stageEnteredAt) => {
    if (!stageEnteredAt) return 0;
    const entered = new Date(stageEnteredAt);
    const now = new Date();
    const diffTime = Math.abs(now - entered);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.customer_email && job.customer_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      job.customer_phone.includes(searchTerm) ||
      (job.address && job.address.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAssignedTo = filterAssignedTo === 'all' || job.assigned_to === filterAssignedTo;
    const matchesTeam = filterTeam === 'all' || job.assigned_team === filterTeam;
    const matchesMarket = filterMarket === 'all' || job.market_id === filterMarket;
    const matchesPriority = filterPriority === 'all' || job.priority === filterPriority;
    
    return matchesSearch && matchesAssignedTo && matchesTeam && matchesMarket && matchesPriority;
  });

  const getJobsByStage = (stageId) => {
    return filteredJobs.filter(job => normalizeJobStage(job) === stageId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading board...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Lead Pipeline Board</h1>
            <p className="text-muted-foreground">Track and manage your leads through the workflow</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold">
            <Plus className="h-4 w-4 mr-2" />
            Create Job
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-1">
                <Label className="text-xs text-white">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-9 text-white placeholder:text-gray-300"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-white">Assigned To</Label>
                <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white">Team</Label>
                <Select value={filterTeam} onValueChange={setFilterTeam}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white">Market</Label>
                <Select value={filterMarket} onValueChange={setFilterMarket}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Markets</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white">Priority</Label>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Board */}
        <div className="overflow-x-auto pb-4">
          <div className="inline-flex gap-4 min-w-full">
            {STAGES.map((stage, index) => {
              const stageJobs = getJobsByStage(stage.id);
              const isPaidStage = stage.id === 'paid';
              
              return (
                <div 
                  key={stage.id}
                  className={`flex-shrink-0 w-80 ${isPaidStage ? 'opacity-60' : ''}`}
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(stage.id)}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  <div className={`${stage.color} rounded-lg p-3 ${dragOverStage === stage.id ? 'ring-2 ring-blue-600 shadow-lg' : ''}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm">{stage.name}</h3>
                      <Badge variant="secondary" className="text-xs">{stageJobs.length}</Badge>
                    </div>
                    
                    <div className="space-y-2 min-h-[500px]">
                      {stageJobs.map(job => (
                        <div
                          key={job.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, job)}
                          onDrag={handleDrag}
                          onDragEnd={handleDragEnd}
                          onClick={() => openJobDetails(job)}
                          className={`bg-card dark:bg-blue-900/40 rounded-lg p-3 shadow-sm hover:shadow-md cursor-move ${
                            draggedCard?.id === job.id 
                              ? 'border-2 border-dashed border-blue-400 dark:border-blue-600' 
                              : 'border border-border dark:border-blue-800/50'
                          }`}
                          style={{
                            opacity: draggedCard?.id === job.id ? 0.4 : 1,
                            transition: 'opacity 0.15s ease-out'
                          }}
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <h4 className="font-semibold text-sm">{job.customer_name}</h4>
                              <div className={`w-2 h-2 rounded-full ${getPriorityColor(job.priority)}`} />
                            </div>
                            
                            {job.job_type && (
                              <p className="text-xs text-muted-foreground">{job.job_type}</p>
                            )}
                            
                            {job.address && (
                              <p className="text-xs text-muted-foreground line-clamp-1">{job.address}</p>
                            )}
                            
                            {/* Financial Info */}
                            <div className="space-y-1 bg-blue-50 dark:bg-blue-900/30 p-2 rounded border border-blue-200 dark:border-blue-800">
                              {job.estimated_value && (
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground font-semibold">Est. Lead Value:</span>
                                  <span className="font-bold text-blue-700 dark:text-blue-400">${job.estimated_value.toLocaleString()}</span>
                                </div>
                              )}
                              {job.square_footage && (
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Sq Ft:</span>
                                  <span className="font-medium dark:text-slate-300">{job.square_footage.toLocaleString()} @ $6/ft</span>
                                </div>
                              )}
                              {job.first_visit_discount && (
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">FVD:</span>
                                  <span className="font-medium text-blue-700 dark:text-blue-400">${job.first_visit_discount.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Dates */}
                            <div className="space-y-1">
                              {job.demo_date && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Demo: {new Date(job.demo_date).toLocaleDateString()}
                                </div>
                              )}
                              {job.install_date && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Install: {new Date(job.install_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            
                            {/* Days in Status */}
                            <div className="flex items-center text-xs text-muted-foreground pt-1 border-t">
                              <Clock className="h-3 w-3 mr-1" />
                              {getDaysInStatus(job.stage_entered_at)} days in stage
                            </div>
                            
                            {/* Action Icons */}
                            <div className="flex items-center justify-between gap-2 pt-1 border-t border-border dark:border-slate-700">
                              <div className="flex items-center gap-1">
                                <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" onClick={(e) => { e.stopPropagation(); }}>
                                  <Phone className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                                </button>
                                <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" onClick={(e) => { e.stopPropagation(); }}>
                                  <Mail className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                                </button>
                              </div>
                              <div className="flex items-center gap-1">
                                {job.photos && job.photos.length > 0 && (
                                  <button className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded relative" onClick={(e) => { e.stopPropagation(); }}>
                                    <Image className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center">
                                      {job.photos.length}
                                    </span>
                                  </button>
                                )}
                                {job.attachments && job.attachments.length > 0 && (
                                  <button className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded relative" onClick={(e) => { e.stopPropagation(); }}>
                                    <File className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center">
                                      {job.attachments.length}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Create Job Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Job</DialogTitle>
              <DialogDescription>Add a new job to the pipeline</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateJob} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Job Type *</Label>
                  <Input
                    value={formData.job_type}
                    onChange={(e) => setFormData({...formData, job_type: e.target.value})}
                    placeholder="e.g., Exterior Coating, Roof"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Square Footage</Label>
                  <Input
                    type="number"
                    value={formData.square_footage}
                    onChange={(e) => {
                      const sqft = e.target.value;
                      setFormData({
                        ...formData, 
                        square_footage: sqft,
                        estimated_value: sqft ? (parseFloat(sqft) * 6).toFixed(2) : ''
                      });
                    }}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Enter house size</p>
                </div>
                <div>
                  <Label>Estimated Value (Auto-Calculated)</Label>
                  <Input
                    type="number"
                    value={formData.estimated_value}
                    onChange={(e) => setFormData({...formData, estimated_value: e.target.value})}
                    placeholder="0.00"
                    className="bg-blue-50"
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    ${formData.square_footage ? (parseFloat(formData.square_footage) * 6).toLocaleString() : '0'} @ $6/sq ft
                  </p>
                </div>
                <div>
                  <Label>First Visit Discount (FVD)</Label>
                  <Input
                    type="number"
                    value={formData.first_visit_discount}
                    onChange={(e) => setFormData({...formData, first_visit_discount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Demo Date</Label>
                  <Input
                    type="date"
                    value={formData.demo_date}
                    onChange={(e) => setFormData({...formData, demo_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Install Date</Label>
                  <Input
                    type="date"
                    value={formData.install_date}
                    onChange={(e) => setFormData({...formData, install_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Assign To</Label>
                  <Select value={formData.assigned_to} onValueChange={(value) => setFormData({...formData, assigned_to: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign Team</Label>
                  <Select value={formData.assigned_team} onValueChange={(value) => setFormData({...formData, assigned_team: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Market</Label>
                  <Select value={formData.market_id} onValueChange={(value) => setFormData({...formData, market_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select market" />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map(market => (
                        <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Create Job</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Job Details Dialog - Draggable */}
        {selectedJob && (
          <DraggableDialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
            <DraggableDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DraggableDialogHeader className="pb-2">
                <div className="flex items-start justify-between gap-4">
                  {/* Left - Contact Info & Buttons */}
                  <div className="flex-shrink-0">
                    <DraggableDialogTitle className="text-2xl">{selectedJob.customer_name}</DraggableDialogTitle>
                    <DraggableDialogDescription>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline">{STAGES.find(s => s.id === selectedJob.stage)?.name}</Badge>
                        <Badge className={`${getPriorityColor(selectedJob.priority)} text-white`}>
                          {selectedJob.priority}
                        </Badge>
                        <span className="text-xs flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getDaysInStatus(selectedJob.stage_entered_at)} days in stage
                        </span>
                      </div>
                    </DraggableDialogDescription>
                    {/* Action buttons */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant={isEditing ? "default" : "outline"}
                        onClick={() => setIsEditing(!isEditing)}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        {isEditing ? 'Cancel' : 'Edit'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setDetailDialogOpen(false);
                          navigate(`/jobs/${selectedJob.id}`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open Job
                      </Button>
                    </div>
                  </div>
                  
                  {/* Right - Quick Message SMS */}
                  <div className="flex-1 max-w-[25rem] mr-8">
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex gap-2">
                        <Textarea
                          placeholder={`SMS to ${selectedJob.customer_phone || 'customer'}...`}
                          value={smsMessage}
                          onChange={(e) => setSmsMessage(e.target.value)}
                          rows={4}
                          className="flex-1 bg-white dark:bg-slate-800 text-sm"
                          disabled={!selectedJob.customer_phone}
                        />
                        <Button 
                          onClick={handleSendSms}
                          disabled={!smsMessage.trim() || !selectedJob.customer_phone || sendingSms}
                          size="sm"
                          className="self-end"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                      {!selectedJob.customer_phone && (
                        <p className="text-xs text-red-500 mt-1">No phone number</p>
                      )}
                    </div>
                    {/* Action buttons under SMS */}
                    <div className="flex gap-2 mt-2">
                      <Button 
                        size="sm" 
                        variant="default"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => window.open(`tel:${selectedJob.customer_phone}`, '_self')}
                        disabled={!selectedJob.customer_phone}
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        Call
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => window.open(`mailto:${selectedJob.customer_email}`, '_self')}
                        disabled={!selectedJob.customer_email}
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Email
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => {
                          setDetailDialogOpen(false);
                          navigate(`/jobs/${selectedJob.id}?tab=communications`);
                        }}
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Communications
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-950"
                        onClick={() => navigate('/communications/social-media')}
                      >
                        <Share2 className="h-4 w-4 mr-1" />
                        Socials
                      </Button>
                    </div>
                  </div>
                </div>
              </DraggableDialogHeader>

              {/* Tabs */}
              <Tabs value={dialogTab} onValueChange={setDialogTab} className="w-full">
                <div className="overflow-x-auto">
                  <TabsList className="h-10 bg-slate-100 dark:bg-slate-800 gap-1 flex-nowrap inline-flex min-w-max mb-4">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <User className="h-4 w-4 mr-1" />
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value="info" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <FileText className="h-4 w-4 mr-1" />
                      Info
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <StickyNote className="h-4 w-4 mr-1" />
                      Notes
                    </TabsTrigger>
                    <TabsTrigger value="ops" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <Wrench className="h-4 w-4 mr-1" />
                      Ops View
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <Activity className="h-4 w-4 mr-1" />
                      Activity
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <ClipboardList className="h-4 w-4 mr-1" />
                      Tasks
                    </TabsTrigger>
                    <TabsTrigger value="files" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap">
                      <File className="h-4 w-4 mr-1" />
                      Files
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6 mt-0">
                  {/* Contact Information */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    {isEditing ? (
                      <>
                        <div>
                          <Label className="text-xs text-muted-foreground">Customer Name</Label>
                          <Input
                            value={editForm.customer_name}
                            onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Phone</Label>
                          <Input
                            value={editForm.customer_phone}
                            onChange={(e) => setEditForm({...editForm, customer_phone: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <Input
                            value={editForm.customer_email}
                            onChange={(e) => setEditForm({...editForm, customer_email: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Address</Label>
                          <Input
                            value={editForm.address}
                            onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">City</Label>
                          <Input
                            value={editForm.city}
                            onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-muted-foreground">State</Label>
                            <Input
                              value={editForm.state}
                              onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Zip</Label>
                            <Input
                              value={editForm.zip_code}
                              onChange={(e) => setEditForm({...editForm, zip_code: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label className="text-xs text-muted-foreground">Phone</Label>
                          <p className="text-sm font-medium">{selectedJob.customer_phone}</p>
                        </div>
                        {selectedJob.customer_email && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Email</Label>
                            <p className="text-sm font-medium">{selectedJob.customer_email}</p>
                          </div>
                        )}
                        {selectedJob.address && (
                          <div className="col-span-2">
                            <Label className="text-xs text-muted-foreground">Address</Label>
                            <p className="text-sm font-medium">
                              {selectedJob.address}
                              {selectedJob.city && `, ${selectedJob.city}`}
                              {selectedJob.state && `, ${selectedJob.state}`}
                              {selectedJob.zip_code && ` ${selectedJob.zip_code}`}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Job Details */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Job Details
                  </h3>
                  <div className="grid grid-cols-3 gap-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    {isEditing ? (
                      <>
                        <div>
                          <Label className="text-xs text-muted-foreground">Job Type</Label>
                          <Input
                            value={editForm.job_type}
                            onChange={(e) => setEditForm({...editForm, job_type: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Estimated Value</Label>
                          <Input
                            type="number"
                            value={editForm.estimated_value}
                            onChange={(e) => setEditForm({...editForm, estimated_value: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Square Footage</Label>
                          <Input
                            type="number"
                            value={editForm.square_footage}
                            onChange={(e) => setEditForm({...editForm, square_footage: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Priority</Label>
                          <Select value={editForm.priority} onValueChange={(v) => setEditForm({...editForm, priority: v})}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Demo Date</Label>
                          <Input
                            type="date"
                            value={editForm.demo_date ? editForm.demo_date.split('T')[0] : ''}
                            onChange={(e) => setEditForm({...editForm, demo_date: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Install Date</Label>
                          <Input
                            type="date"
                            value={editForm.install_date ? editForm.install_date.split('T')[0] : ''}
                            onChange={(e) => setEditForm({...editForm, install_date: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {selectedJob.job_type && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Job Type</Label>
                            <p className="text-sm font-medium">{selectedJob.job_type}</p>
                          </div>
                        )}
                        {selectedJob.estimated_value && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Estimated Value</Label>
                            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">${selectedJob.estimated_value.toLocaleString()}</p>
                          </div>
                        )}
                        {selectedJob.square_footage && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Square Footage</Label>
                            <p className="text-sm font-medium">{selectedJob.square_footage.toLocaleString()} sq ft</p>
                          </div>
                        )}
                        {selectedJob.first_visit_discount && (
                          <div>
                            <Label className="text-xs text-muted-foreground">First Visit Discount</Label>
                            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">${selectedJob.first_visit_discount.toLocaleString()}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {isEditing && (
                    <div className="mt-3 flex justify-end">
                      <Button onClick={handleSaveEdit} size="sm">
                        <Save className="h-4 w-4 mr-1" />
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>

                {/* Important Dates */}
                {(selectedJob.demo_date || selectedJob.install_date || selectedJob.appointment_date) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Important Dates
                    </h3>
                    <div className="grid grid-cols-3 gap-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                      {selectedJob.appointment_date && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Appointment</Label>
                          <p className="text-sm font-medium">{new Date(selectedJob.appointment_date).toLocaleDateString()}</p>
                        </div>
                      )}
                      {selectedJob.demo_date && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Demo Date</Label>
                          <p className="text-sm font-medium">{new Date(selectedJob.demo_date).toLocaleDateString()}</p>
                        </div>
                      )}
                      {selectedJob.install_date && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Install Date</Label>
                          <p className="text-sm font-medium">{new Date(selectedJob.install_date).toLocaleDateString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Assignment */}
                {(selectedJob.assigned_to || selectedJob.assigned_team) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Assignment</h3>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                      {selectedJob.assigned_to && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Assigned To</Label>
                          <p className="text-sm font-medium">
                            {users.find(u => u.id === selectedJob.assigned_to)?.first_name} {users.find(u => u.id === selectedJob.assigned_to)?.last_name}
                          </p>
                        </div>
                      )}
                      {selectedJob.assigned_team && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Team</Label>
                          <p className="text-sm font-medium">
                            {teams.find(t => t.id === selectedJob.assigned_team)?.name}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Documents & Photos */}
                {((selectedJob.photos && selectedJob.photos.length > 0) || (selectedJob.attachments && selectedJob.attachments.length > 0)) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Documents & Photos</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedJob.photos && selectedJob.photos.length > 0 && (
                        <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Image className="h-4 w-4 text-blue-600" />
                            <Label className="text-xs font-semibold">Photos ({selectedJob.photos.length})</Label>
                          </div>
                          <p className="text-xs text-muted-foreground">Photo management coming soon</p>
                        </div>
                      )}
                      {selectedJob.attachments && selectedJob.attachments.length > 0 && (
                        <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <File className="h-4 w-4 text-green-600" />
                            <Label className="text-xs font-semibold">Documents ({selectedJob.attachments.length})</Label>
                          </div>
                          <p className="text-xs text-muted-foreground">Document management coming soon</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedJob.notes && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Initial Notes</h3>
                    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                      <p className="text-sm">{selectedJob.notes}</p>
                    </div>
                  </div>
                )}

                {/* Activity & Notes */}
                <div>
                  <h3 className="text-sm font-semibold mb-3">Activity & Notes</h3>
                  
                  {/* Add Note */}
                  <div className="mb-4">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Add a note or comment..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        size="sm"
                        className="self-end"
                      >
                        Add Note
                      </Button>
                    </div>
                  </div>

                  {/* Activity Log */}
                  {selectedJob.activity_log && selectedJob.activity_log.length > 0 && (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedJob.activity_log.slice().reverse().map((activity, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border-l-2 ${
                            activity.type === 'stage_change' 
                              ? 'bg-blue-50 dark:bg-blue-950 border-blue-600' 
                              : 'bg-slate-50 dark:bg-slate-900 border-green-600'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {activity.type === 'stage_change' && (
                                <Badge variant="outline" className="text-xs">Stage Change</Badge>
                              )}
                              <span className="text-xs font-semibold">{activity.user_name}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(activity.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm">{activity.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(!selectedJob.activity_log || selectedJob.activity_log.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No activity yet. Be the first to add a note!
                    </p>
                  )}
                </div>

                </TabsContent>

                {/* Info Tab - Full Data View */}
                <TabsContent value="info" className="space-y-4 mt-0 max-h-[60vh] overflow-y-auto pr-2">
                  {/* Customer Information */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Customer Information
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block">Name</span>
                        <span className="font-medium">{selectedJob.customer_name || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Phone</span>
                        <span className="font-medium">{selectedJob.customer_phone || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Email</span>
                        <span className="font-medium">{selectedJob.customer_email || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Address
                    </h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div className="col-span-2">
                        <span className="text-muted-foreground block">Street Address</span>
                        <span className="font-medium">{selectedJob.address || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">City</span>
                        <span className="font-medium">{selectedJob.city || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-muted-foreground block">State</span>
                          <span className="font-medium">{selectedJob.state || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Zip</span>
                          <span className="font-medium">{selectedJob.zip_code || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Project Details
                    </h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block">Job Type</span>
                        <span className="font-medium">{selectedJob.job_type || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Status</span>
                        <Badge variant="outline">{STAGES.find(s => s.id === selectedJob.stage)?.name || selectedJob.stage}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Priority</span>
                        <Badge className={`${getPriorityColor(selectedJob.priority)} text-white`}>{selectedJob.priority || 'N/A'}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Market</span>
                        <span className="font-medium">{selectedJob.market_id || 'N/A'}</span>
                      </div>
                    </div>
                    {selectedJob.description && (
                      <div className="mt-3">
                        <span className="text-muted-foreground block text-sm">Description</span>
                        <p className="text-sm">{selectedJob.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Financial Information */}
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Financial Information
                    </h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block">Estimated Value</span>
                        <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">${selectedJob.estimated_value?.toLocaleString() || '0'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Contract Amount</span>
                        <span className="font-medium">${selectedJob.contract_amount?.toLocaleString() || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Square Footage</span>
                        <span className="font-medium">{selectedJob.square_footage?.toLocaleString() || 'N/A'} sq ft</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">First Visit Discount</span>
                        <span className="font-medium">${selectedJob.first_visit_discount?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Important Dates
                    </h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block">Demo Date</span>
                        <span className="font-medium">{selectedJob.demo_date ? new Date(selectedJob.demo_date).toLocaleDateString() : 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Install Date</span>
                        <span className="font-medium">{selectedJob.install_date ? new Date(selectedJob.install_date).toLocaleDateString() : 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Est. Start Date</span>
                        <span className="font-medium">{selectedJob.estimated_start_date ? new Date(selectedJob.estimated_start_date).toLocaleDateString() : 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Completed Date</span>
                        <span className="font-medium">{selectedJob.completed_date ? new Date(selectedJob.completed_date).toLocaleDateString() : 'Not set'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Personnel */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Personnel
                    </h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block">Assigned To</span>
                        <span className="font-medium">{selectedJob.assigned_to_name || selectedJob.assigned_to || 'Unassigned'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Project Manager</span>
                        <span className="font-medium">{selectedJob.project_manager || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Foreman</span>
                        <span className="font-medium">{selectedJob.foreman || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Assigned Crew</span>
                        <span className="font-medium">{selectedJob.assigned_crew || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Job Specifics */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Job Specifics
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block">Colors</span>
                        <span className="font-medium">{selectedJob.colors || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Product Used</span>
                        <span className="font-medium">{selectedJob.product_used || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Warranty</span>
                        <span className="font-medium">{selectedJob.warranty || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Payment Plan</span>
                        <span className="font-medium">{selectedJob.payment_plan || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">HOA Info</span>
                        <span className="font-medium">{selectedJob.hoa_info || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Color Change</span>
                        <span className="font-medium">{selectedJob.color_change ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                    {selectedJob.production_notes && (
                      <div className="mt-3">
                        <span className="text-muted-foreground block text-sm">Production Notes</span>
                        <p className="text-sm">{selectedJob.production_notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Open Full Job Button */}
                  <div className="flex justify-center pt-2 sticky bottom-0 bg-background py-2">
                    <Button 
                      onClick={() => {
                        setDetailDialogOpen(false);
                        navigate(`/jobs/${selectedJob.id}?tab=info`);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Edit in Full Job View
                    </Button>
                  </div>
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="space-y-4 mt-0 max-h-[60vh] overflow-y-auto pr-2">
                  {/* Quick Add Note */}
                  <div className="bg-gradient-to-br from-blue-50/50 to-sky-50/50 dark:from-blue-950/30 dark:to-sky-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <StickyNote className="h-4 w-4 text-blue-500" />
                      Quick Add Note
                    </h4>
                    <div className="space-y-3">
                      <div className="relative">
                        <Textarea 
                          placeholder="Write a note... Use @username to tag"
                          className="min-h-[80px] resize-none text-sm"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                        />
                        <div className="absolute bottom-2 right-2 flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" title="Tag User">
                            <AtSign className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-500" title="Tag Crew">
                            <Users className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Button variant="outline" size="sm" className="text-xs gap-1">
                          <CalendarPlus className="h-3 w-3" />
                          Add Event
                        </Button>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs gap-1" onClick={handleAddNote}>
                          <Send className="h-3 w-3" />
                          Post
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Notes List */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <FileText className="h-4 w-4 text-green-500" />
                        Notes History
                      </h4>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="text-xs text-blue-500 p-0 h-auto"
                        onClick={() => {
                          setDetailDialogOpen(false);
                          navigate(`/jobs/${selectedJob.id}?tab=notes`);
                        }}
                      >
                        View All →
                      </Button>
                    </div>
                    {selectedJob.activity_log && selectedJob.activity_log.filter(a => a.type === 'note').length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedJob.activity_log
                          .filter(a => a.type === 'note')
                          .slice()
                          .reverse()
                          .slice(0, 5)
                          .map((note, idx) => (
                            <div 
                              key={idx} 
                              className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                            >
                              <div className="flex items-start gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-sky-400 text-white text-xs">
                                    {note.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold">{note.user_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(note.timestamp).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-sm mt-1 line-clamp-2">{note.note}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <StickyNote className="h-10 w-10 mx-auto mb-2 text-slate-300 dark:text-slate-700" />
                        <p className="text-sm text-muted-foreground">No notes yet</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Ops View Tab */}
                <TabsContent value="ops" className="space-y-4 mt-0 max-h-[60vh] overflow-y-auto pr-2">
                  {/* Crew Assignment */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4 text-green-500" />
                      Crew Assignment
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Assigned Crew</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('assigned_crew');
                              setOpsEditValue(selectedJob.assigned_crew || '');
                            }}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        </div>
                        {editingOpsField === 'assigned_crew' ? (
                          <div className="mt-1 flex gap-1">
                            <Select value={opsEditValue} onValueChange={setOpsEditValue}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select crew" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {crews.map(crew => (
                                  <SelectItem key={crew.id} value={crew.name}>{crew.name}</SelectItem>
                                ))}
                                {crews.length === 0 && (
                                  <>
                                    <SelectItem value="Crew A">Crew A</SelectItem>
                                    <SelectItem value="Crew B">Crew B</SelectItem>
                                    <SelectItem value="Crew C">Crew C</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <Button size="icon" className="h-8 w-8" onClick={() => handleOpsFieldUpdate('assigned_crew', opsEditValue)}>
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingOpsField(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.assigned_crew || 'Not Assigned'}</p>
                        )}
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Install Date</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('install_date');
                              setOpsEditValue(selectedJob.install_date?.split('T')[0] || '');
                            }}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        </div>
                        {editingOpsField === 'install_date' ? (
                          <div className="mt-1 flex gap-1">
                            <Input 
                              type="date" 
                              className="h-8 text-xs" 
                              value={opsEditValue}
                              onChange={(e) => setOpsEditValue(e.target.value)}
                            />
                            <Button size="icon" className="h-8 w-8" onClick={() => handleOpsFieldUpdate('install_date', opsEditValue)}>
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingOpsField(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.install_date ? new Date(selectedJob.install_date).toLocaleDateString() : 'Not Set'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Job Specifications */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Ruler className="h-4 w-4 text-purple-500" />
                      Job Specifications
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Square Footage</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('square_footage');
                              setOpsEditValue(selectedJob.square_footage || '');
                            }}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        </div>
                        {editingOpsField === 'square_footage' ? (
                          <div className="mt-1 flex gap-1">
                            <Input 
                              type="number" 
                              className="h-8 text-xs" 
                              value={opsEditValue}
                              onChange={(e) => setOpsEditValue(e.target.value)}
                              placeholder="Enter sq ft"
                            />
                            <Button size="icon" className="h-8 w-8" onClick={() => handleOpsFieldUpdate('square_footage', parseInt(opsEditValue) || null)}>
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingOpsField(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.square_footage?.toLocaleString() || '—'} sq ft</p>
                        )}
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Job Type</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('job_type');
                              setOpsEditValue(selectedJob.job_type || '');
                            }}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        </div>
                        {editingOpsField === 'job_type' ? (
                          <div className="mt-1 flex gap-1">
                            <Input 
                              className="h-8 text-xs" 
                              value={opsEditValue}
                              onChange={(e) => setOpsEditValue(e.target.value)}
                              placeholder="Job type"
                            />
                            <Button size="icon" className="h-8 w-8" onClick={() => handleOpsFieldUpdate('job_type', opsEditValue)}>
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingOpsField(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.job_type || '—'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Production Status */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Settings className="h-4 w-4 text-orange-500" />
                      Production Status
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded text-center">
                        <span className="text-xs text-muted-foreground">Stage</span>
                        <Badge className="mt-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs">
                          {selectedJob.stage || '—'}
                        </Badge>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded text-center group relative">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xs text-muted-foreground">QC</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('qc_status');
                              setOpsEditValue(selectedJob.qc_status || 'Pending');
                            }}
                          >
                            <Edit2 className="h-2 w-2" />
                          </Button>
                        </div>
                        {editingOpsField === 'qc_status' ? (
                          <div className="mt-1 flex gap-1">
                            <Select value={opsEditValue} onValueChange={(val) => { setOpsEditValue(val); handleOpsFieldUpdate('qc_status', val); }}>
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pending">Pending</SelectItem>
                                <SelectItem value="In Progress">In Progress</SelectItem>
                                <SelectItem value="Passed">Passed</SelectItem>
                                <SelectItem value="Failed">Failed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <Badge variant="outline" className="mt-1 text-xs">
                            {selectedJob.qc_status || 'Pending'}
                          </Badge>
                        )}
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded text-center group relative">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xs text-muted-foreground">Materials</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleOpsFieldUpdate('materials_ordered', !selectedJob.materials_ordered)}
                          >
                            <Edit2 className="h-2 w-2" />
                          </Button>
                        </div>
                        <Badge variant={selectedJob.materials_ordered ? 'default' : 'secondary'} className="mt-1 text-xs cursor-pointer" onClick={() => handleOpsFieldUpdate('materials_ordered', !selectedJob.materials_ordered)}>
                          {selectedJob.materials_ordered ? 'Ordered' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Scheduling */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      Key Dates
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Demo</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('demo_date');
                              setOpsEditValue(selectedJob.demo_date?.split('T')[0] || '');
                            }}
                          >
                            <Edit2 className="h-2 w-2" />
                          </Button>
                        </div>
                        {editingOpsField === 'demo_date' ? (
                          <div className="mt-1 flex gap-1">
                            <Input 
                              type="date" 
                              className="h-7 text-xs" 
                              value={opsEditValue}
                              onChange={(e) => setOpsEditValue(e.target.value)}
                            />
                            <Button size="icon" className="h-7 w-7" onClick={() => handleOpsFieldUpdate('demo_date', opsEditValue)}>
                              <Save className="h-2 w-2" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.demo_date ? new Date(selectedJob.demo_date).toLocaleDateString() : '—'}</p>
                        )}
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Install</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('install_date_key');
                              setOpsEditValue(selectedJob.install_date?.split('T')[0] || '');
                            }}
                          >
                            <Edit2 className="h-2 w-2" />
                          </Button>
                        </div>
                        {editingOpsField === 'install_date_key' ? (
                          <div className="mt-1 flex gap-1">
                            <Input 
                              type="date" 
                              className="h-7 text-xs" 
                              value={opsEditValue}
                              onChange={(e) => setOpsEditValue(e.target.value)}
                            />
                            <Button size="icon" className="h-7 w-7" onClick={() => handleOpsFieldUpdate('install_date', opsEditValue)}>
                              <Save className="h-2 w-2" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.install_date ? new Date(selectedJob.install_date).toLocaleDateString() : '—'}</p>
                        )}
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded group relative">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Inspection</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setEditingOpsField('inspection_date');
                              setOpsEditValue(selectedJob.inspection_date?.split('T')[0] || '');
                            }}
                          >
                            <Edit2 className="h-2 w-2" />
                          </Button>
                        </div>
                        {editingOpsField === 'inspection_date' ? (
                          <div className="mt-1 flex gap-1">
                            <Input 
                              type="date" 
                              className="h-7 text-xs" 
                              value={opsEditValue}
                              onChange={(e) => setOpsEditValue(e.target.value)}
                            />
                            <Button size="icon" className="h-7 w-7" onClick={() => handleOpsFieldUpdate('inspection_date', opsEditValue)}>
                              <Save className="h-2 w-2" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{selectedJob.inspection_date ? new Date(selectedJob.inspection_date).toLocaleDateString() : '—'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* View Full Ops */}
                  <div className="pt-2">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        setDetailDialogOpen(false);
                        navigate(`/jobs/${selectedJob.id}?tab=ops`);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Full Ops Details
                    </Button>
                  </div>
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="space-y-4 mt-0 max-h-[60vh] overflow-y-auto pr-2">
                  
                  {/* Communications Section */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-500" />
                      Communications
                    </h4>
                    {conversations && conversations.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {conversations.slice(0, 10).map((conv, idx) => (
                          <div 
                            key={idx} 
                            className="p-2 rounded border-l-2 border-blue-500 bg-white dark:bg-slate-800"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <Badge variant="outline" className="text-xs">
                                {conv.channel || 'SMS'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString() : 'N/A'}
                              </span>
                            </div>
                            <p className="text-sm truncate">
                              {conv.last_message || conv.messages?.[conv.messages?.length - 1]?.content || 'No message content'}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-3">
                        No communications yet
                      </p>
                    )}
                  </div>

                  {/* Notes Section - Placeholder */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-500" />
                      Notes
                    </h4>
                    {selectedJob.activity_log && selectedJob.activity_log.filter(a => a.type === 'note').length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedJob.activity_log
                          .filter(a => a.type === 'note')
                          .slice()
                          .reverse()
                          .map((note, idx) => (
                            <div 
                              key={idx} 
                              className="p-2 rounded border-l-2 border-green-500 bg-white dark:bg-slate-800"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold">{note.user_name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(note.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm">{note.note}</p>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-3">
                        No notes yet
                      </p>
                    )}
                  </div>

                  {/* Status Changes Section */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-purple-500" />
                      Status Changes
                    </h4>
                    {selectedJob.activity_log && selectedJob.activity_log.filter(a => a.type === 'stage_change').length > 0 ? (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {(() => {
                          const stageChanges = selectedJob.activity_log
                            .filter(a => a.type === 'stage_change')
                            .slice()
                            .reverse();
                          const currentStageIndex = STAGES.findIndex(s => s.id === selectedJob.stage);
                          const nextStage = currentStageIndex < STAGES.length - 1 ? STAGES[currentStageIndex + 1] : null;
                          
                          return stageChanges.map((change, idx) => {
                            // Parse "Moved from X to Y" to extract stages
                            const moveMatch = change.note?.match(/Moved from (\S+) to (\S+)/i);
                            const fromStage = moveMatch ? moveMatch[1] : null;
                            const toStage = moveMatch ? moveMatch[2] : null;
                            const fromStageName = STAGES.find(s => s.id === fromStage)?.name || fromStage;
                            const toStageName = STAGES.find(s => s.id === toStage)?.name || toStage;
                            const isLatestChange = idx === 0;
                            
                            // Find the actual user to get profile picture and full name
                            const activityUser = users.find(u => 
                              u.email === change.user_name || 
                              u.id === change.user_id ||
                              `${u.first_name} ${u.last_name}` === change.user_name
                            );
                            const displayName = activityUser 
                              ? `${activityUser.first_name} ${activityUser.last_name}`
                              : change.user_name?.split('@')[0] || 'Unknown';
                            // Build the full avatar URL - profile_picture is a relative path
                            const profilePicPath = activityUser?.avatar_url || activityUser?.profile_picture;
                            const avatarUrl = profilePicPath 
                              ? (profilePicPath.startsWith('http') ? profilePicPath : `${BACKEND_URL}${profilePicPath}`)
                              : null;
                            const initials = activityUser 
                              ? `${activityUser.first_name?.[0] || ''}${activityUser.last_name?.[0] || ''}`
                              : change.user_name?.split('@')[0]?.slice(0, 2).toUpperCase() || 'U';
                            
                            return (
                              <div 
                                key={idx} 
                                className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                              >
                                <div className="flex items-center gap-3">
                                  {/* Profile Picture */}
                                  <Avatar className="h-9 w-9 flex-shrink-0 border-2 border-purple-200 dark:border-purple-800">
                                    <AvatarImage src={avatarUrl} alt={displayName} />
                                    <AvatarFallback className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white text-xs font-semibold">
                                      {initials}
                                    </AvatarFallback>
                                  </Avatar>
                                  
                                  {/* User Name + Status Change with Arrows + Timestamp */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1 mb-1">
                                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                                        {displayName}
                                      </span>
                                    </div>
                                    
                                    {/* Status Flow with Arrows */}
                                    {fromStage && toStage ? (
                                      <div className="flex items-center gap-1 flex-wrap">
                                        {/* Previous Status */}
                                        <Badge variant="outline" className="text-xs bg-slate-100 dark:bg-slate-700">
                                          {fromStageName}
                                        </Badge>
                                        
                                        {/* Green Arrow - from last to current (completed transition) */}
                                        <div className="relative">
                                          <ArrowRight 
                                            className="h-4 w-4 text-green-500" 
                                            style={{
                                              filter: 'drop-shadow(0 0 4px rgb(34 197 94)) drop-shadow(0 0 8px rgb(34 197 94 / 0.5))'
                                            }}
                                          />
                                        </div>
                                        
                                        {/* Current Status */}
                                        <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-300 dark:border-green-700">
                                          {toStageName}
                                        </Badge>
                                        
                                        {/* Yellow Arrow - to next status (only on latest change) */}
                                        {isLatestChange && nextStage && (
                                          <>
                                            <div className="relative">
                                              <ArrowRight 
                                                className="h-4 w-4 text-yellow-500 animate-pulse" 
                                                style={{
                                                  filter: 'drop-shadow(0 0 4px rgb(234 179 8)) drop-shadow(0 0 8px rgb(234 179 8 / 0.5))'
                                                }}
                                              />
                                            </div>
                                            <Badge variant="outline" className="text-xs bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700 border-dashed">
                                              {nextStage.name}
                                            </Badge>
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-sm text-slate-600 dark:text-slate-400">
                                        {change.note}
                                      </span>
                                    )}
                                    
                                    <span className="text-xs text-muted-foreground block mt-1">
                                      {new Date(change.timestamp).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-3">
                        No status changes recorded
                      </p>
                    )}
                  </div>

                  {/* Automations Run Section - Placeholder */}
                  <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-orange-500" />
                      Automations Run
                    </h4>
                    <p className="text-sm text-muted-foreground text-center py-3">
                      Automation tracking coming soon
                    </p>
                  </div>

                </TabsContent>

                {/* Tasks Tab */}
                <TabsContent value="tasks" className="space-y-6 mt-0">
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Task management coming soon</p>
                  </div>
                </TabsContent>

                {/* Files Tab */}
                <TabsContent value="files" className="space-y-6 mt-0">
                  <div className="text-center py-8 text-muted-foreground">
                    <File className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>File management coming soon</p>
                  </div>
                </TabsContent>

              </Tabs>

              <DraggableDialogFooter className="mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setDetailDialogOpen(false)}
                >
                  Close
                </Button>
              </DraggableDialogFooter>
            </DraggableDialogContent>
          </DraggableDialog>
        )}
      </div>

      {/* Floating Drag Preview - Full Card */}
      {draggedCard && isDragging && (
        <div
          className="fixed pointer-events-none z-[9999] top-0 left-0"
          style={{
            transform: `translate3d(${dragPosition.x - 110}px, ${dragPosition.y - 80}px, 0)`,
            width: '220px',
          }}
        >
          <div 
            className="bg-card dark:bg-blue-900/90 rounded-lg p-3 border-2 border-blue-500 shadow-[0_0_30px_rgba(37,99,235,0.8)]"
            style={{
              transform: dragDirection === 'forward' 
                ? 'rotate(5deg)' 
                : dragDirection === 'backward' 
                  ? 'rotate(-5deg)' 
                  : 'rotate(0deg)',
            }}
          >
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <h4 className="font-semibold text-sm">{draggedCard.customer_name}</h4>
                <div className={`w-2 h-2 rounded-full ${getPriorityColor(draggedCard.priority)}`} />
              </div>
              
              {draggedCard.job_type && (
                <p className="text-xs text-muted-foreground">{draggedCard.job_type}</p>
              )}
              
              {draggedCard.address && (
                <p className="text-xs text-muted-foreground line-clamp-1">{draggedCard.address}</p>
              )}
              
              {/* Financial Info */}
              <div className="space-y-1 bg-blue-50 dark:bg-blue-900/30 p-2 rounded border border-blue-200 dark:border-blue-800">
                {draggedCard.estimated_value && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground font-semibold">Est. Lead Value:</span>
                    <span className="font-bold text-blue-700 dark:text-blue-400">${draggedCard.estimated_value.toLocaleString()}</span>
                  </div>
                )}
                {draggedCard.square_footage && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Sq Ft:</span>
                    <span className="font-medium dark:text-slate-300">{draggedCard.square_footage.toLocaleString()} @ $6/ft</span>
                  </div>
                )}
                {draggedCard.first_visit_discount && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">FVD:</span>
                    <span className="font-medium text-blue-700 dark:text-blue-400">${draggedCard.first_visit_discount.toLocaleString()}</span>
                  </div>
                )}
              </div>
              
              {/* Dates */}
              <div className="space-y-1">
                {draggedCard.demo_date && (
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    Demo: {new Date(draggedCard.demo_date).toLocaleDateString()}
                  </div>
                )}
                {draggedCard.install_date && (
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    Install: {new Date(draggedCard.install_date).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              {/* Days in Status */}
              <div className="flex items-center text-xs text-muted-foreground pt-1 border-t border-border dark:border-slate-700">
                <Clock className="h-3 w-3 mr-1" />
                {getDaysInStatus(draggedCard.stage_entered_at)} days in stage
              </div>
              
              {/* Action Icons */}
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-border dark:border-slate-700">
                <div className="flex items-center gap-1">
                  <div className="p-1">
                    <Phone className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div className="p-1">
                    <Mail className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {draggedCard.photos && draggedCard.photos.length > 0 && (
                    <div className="p-1 relative">
                      <Image className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center">
                        {draggedCard.photos.length}
                      </span>
                    </div>
                  )}
                  {draggedCard.attachments && draggedCard.attachments.length > 0 && (
                    <div className="p-1 relative">
                      <File className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center">
                        {draggedCard.attachments.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
